// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, Feedback, ReconciliationItem } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogUpdateReconciliationItemStatusProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogUpdateReconciliationItemStatus = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogUpdateReconciliationItemStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogReconciliationItem = searchParams.get('dialogReconciliationItem');
	const paramDialogStatus = searchParams.get('dialogStatus');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [reconciliationItem, setReconciliationItem] = useState<ReconciliationItem | null>(null);

	// fetch reconciliation item
	const { isLoading: isLoadingReconciliationItem } = useFetch({
		isEnabled: Boolean(paramDialogReconciliationItem && isOpen),
		url: `${process.env.REACT_APP_API_URL}/reconciliation-items/${paramDialogReconciliationItem}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setReconciliationItem(res.data[0]);
			}
		},
	});

	// vars
	const dialogActionProps: Partial<DialogActionsProps> = {};
	const isActivating = paramDialogStatus === 'ACTIVE';

	// deactivate translations
	const dialog = {
		alert: t('alert.deactivateReconciliationItem'),
		btnSubmit: {
			children: t('deactivateReconciliationItem'),
			color: 'error',
		},
		i18nKey: 'dialogUpdateReconciliationItemStatus.deactivate.content',
		title: t('deactivateReconciliationItem'),
	};

	// activate translations
	if (isActivating) {
		dialog.btnSubmit = {
			children: t('activateReconciliationItem'),
			color: 'primary',
		};
		dialog.alert = t('alert.activateReconciliationItem');
		dialog.i18nKey = 'dialogUpdateReconciliationItemStatus.activate.content';
		dialog.title = t('activateReconciliationItem');
	}

	if (reconciliationItem) {
		dialogActionProps.subtitle = {
			children: reconciliationItem.name,
		};
	}

	// patch reconciliation item
	const { fetchRequest: updateReconciliationItem, isLoading: isLoadingUpdateReconciliationItem } =
		useFetch({
			isLazy: true,
			url: `${process.env.REACT_APP_API_URL}/reconciliation-items/${paramDialogReconciliationItem}`,
			options: {
				method: 'PATCH',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					status: paramDialogStatus,
				}),
			},
			onError: (feedback) => {
				const feedbackNew = {
					alert: {
						children: t('error.default'),
					},
				};

				if (feedback.id === 'RECONCILIATION_ITEM_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('feedback.unauthorized');
				} else if (feedback.id === 'RECONCILIATION_ITEM_DEFAULT_CANNOT_EDIT') {
					feedbackNew.alert.children = t('feedback.reconciliationItemDefaultCannotEdit');
				}

				setFeedback(feedbackNew);
			},
			onSuccess: () => {
				let message = t('alert.reconciliationItemDeactivated', { name: reconciliationItem?.name });

				if (isActivating) {
					message = t('alert.reconciliationItemActivated', { name: reconciliationItem?.name });
				}

				// set banner
				setBanner({
					hasClose: true,
					message,
					severity: 'success',
				});

				if (onSuccess) {
					onSuccess();
				}

				// close dialog
				if (onClose) {
					onClose();
				}
			},
		});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// update reconciliation item
		updateReconciliationItem();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingReconciliationItem) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
						values={{ name: reconciliationItem?.name }}
					/>
				</Grid>
				{dialog.alert && (
					<Grid item xs={12}>
						<Alert severity="warning">{dialog.alert}</Alert>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...(dialog.btnSubmit as ActionButton),
					loading: isLoadingUpdateReconciliationItem,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'form',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			title={{
				children: dialog.title,
			}}
			{...dialogActionProps}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
